/**
 * usePWAInstall — shared hook that exposes:
 *   - isStandalone: already installed (running from home screen)
 *   - canInstall: the browser fired `beforeinstallprompt` and we captured it
 *   - isIos: running on iOS Safari (no install API — show manual hint)
 *   - install(): triggers the native prompt (returns the user's choice string)
 */
import { useEffect, useState, useCallback } from 'react';

function detectIos() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
}

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// Module-level cache so every component shares the latest prompt.
let _deferredPrompt = null;
const _listeners = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    _listeners.forEach((fn) => fn(e));
  });
  window.addEventListener('appinstalled', () => {
    _deferredPrompt = null;
    _listeners.forEach((fn) => fn(null));
  });
}

export default function usePWAInstall() {
  const [deferred, setDeferred] = useState(_deferredPrompt);
  const [standalone, setStandalone] = useState(detectStandalone);
  const isIos = detectIos();

  useEffect(() => {
    const fn = (e) => setDeferred(e);
    _listeners.add(fn);
    // Also react to display-mode changes (iOS flips after first open).
    const mm = window.matchMedia?.('(display-mode: standalone)');
    const mmFn = () => setStandalone(detectStandalone());
    mm?.addEventListener?.('change', mmFn);
    return () => {
      _listeners.delete(fn);
      mm?.removeEventListener?.('change', mmFn);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return { outcome: 'unavailable' };
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice?.outcome === 'accepted') {
        _deferredPrompt = null;
        setDeferred(null);
      }
      return choice;
    } catch {
      return { outcome: 'error' };
    }
  }, [deferred]);

  return {
    canInstall: !!deferred && !standalone,
    isStandalone: standalone,
    isIos: isIos && !standalone,
    install,
  };
}
